import React from 'react'
import PropTypes from 'prop-types'
import { IconFolderActive, IconPencilEdit, IconEye } from '@edulastic/icons'
import { tabGrey, themeColor, title, white } from '@edulastic/colors'
import styled from 'styled-components'
import { folderSharing } from '@edulastic/constants'

const { FOLDER_ROLE } = folderSharing

const FOLDER_SIZES = {
  sidebar: { width: 20, height: 20 },
  modal: { width: 24, height: 24 },
}

const BADGE_SIZES = {
  sidebar: { size: 14, icon: 8 },
  modal: { size: 16, icon: 10 },
}

const getLayout = (variant) => {
  const folder = FOLDER_SIZES[variant] || FOLDER_SIZES.sidebar
  const badge = BADGE_SIZES[variant] || BADGE_SIZES.sidebar
  return { folder, badge }
}

const getRoleAriaLabel = (role) => {
  if (role === FOLDER_ROLE.EDITOR) return 'Editor access folder'
  if (role === FOLDER_ROLE.VIEWER) return 'Viewer access folder'
  return 'Folder'
}

const FolderRoleIcon = ({ role, active, variant }) => {
  const { folder, badge } = getLayout(variant)
  const folderColor =
    variant === 'modal' ? title : active ? themeColor : tabGrey
  const normalizedRole = String(role || '').toUpperCase()
  const showEditorBadge = normalizedRole === FOLDER_ROLE.EDITOR
  const showViewerBadge = normalizedRole === FOLDER_ROLE.VIEWER
  const ariaLabel =
    showEditorBadge || showViewerBadge
      ? getRoleAriaLabel(normalizedRole)
      : undefined

  return (
    <IconContainer
      className="folder-role-icon"
      $width={folder.width}
      $height={folder.height}
      aria-hidden={!ariaLabel}
      aria-label={ariaLabel}
    >
      <StyledFolderIcon
        className="folder-role-icon__folder"
        $width={folder.width}
        $height={folder.height}
        $color={folderColor}
      />
      {showEditorBadge && (
        <RoleBadge
          className="folder-role-icon__badge"
          data-cy="folder-editor-badge"
          $size={badge.size}
          $iconSize={badge.icon}
        >
          <IconPencilEdit
            width={badge.icon}
            height={badge.icon}
            color={title}
          />
        </RoleBadge>
      )}
      {showViewerBadge && (
        <RoleBadge
          className="folder-role-icon__badge"
          data-cy="folder-viewer-badge"
          $size={badge.size}
          $iconSize={badge.icon}
        >
          <IconEye width={badge.icon} height={badge.icon} color={title} />
        </RoleBadge>
      )}
    </IconContainer>
  )
}

FolderRoleIcon.propTypes = {
  role: PropTypes.string,
  active: PropTypes.bool,
  variant: PropTypes.oneOf(['sidebar', 'modal']),
}

FolderRoleIcon.defaultProps = {
  role: FOLDER_ROLE.OWNER,
  active: false,
  variant: 'sidebar',
}

export default FolderRoleIcon

const IconContainer = styled.span`
  position: relative;
  display: inline-block;
  width: ${({ $width }) => $width}px;
  min-width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  flex-shrink: 0;
  overflow: visible;
  z-index: 1;
`

const StyledFolderIcon = styled(IconFolderActive)`
  && {
    width: ${({ $width }) => $width}px !important;
    min-width: ${({ $width }) => $width}px !important;
    height: ${({ $height }) => $height}px !important;
    flex-shrink: 0;
    display: block;

    path:first-of-type {
      fill: ${({ $color }) => $color} !important;
    }
  }
`

const RoleBadge = styled.span`
  position: absolute;
  right: ${({ $size }) => -Math.round($size * 0.35)}px;
  bottom: ${({ $size }) => -Math.round($size * 0.2)}px;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${white};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 1px ${tabGrey};
  pointer-events: none;
  z-index: 2;

  svg {
    width: ${({ $iconSize }) => $iconSize}px !important;
    min-width: ${({ $iconSize }) => $iconSize}px !important;
    height: ${({ $iconSize }) => $iconSize}px !important;
    display: block;
    flex-shrink: 0;
  }
`
